import { HttpService,ServerResponse } from "./http"
import { API_BASE_URL } from "../config/environment"
import { extractUserFromToken, getTokenExpiration } from "../lib/jwt"

// 用户相关接口
export interface User {
  id: number
  account: string
  nickname: string | null
  gender: string
  avatar: string | null
  email: string | null
  phoneNumber: string | null
  phoneAreaCode: string | null
  gmtBirthday: number | null
  isBlocked: boolean
  isLocked: boolean
  realName: string | null
  language: string | null
  isUsernameFroze: boolean
  isNeedDestroy: boolean
  gmtDestroyTime: number | null
  gmtDestroyRequest: number | null
  isUnderDestroy: boolean | null
  gmtDestroy: number | null
  userMetaModel: any | null
  departmentModelList: any[] | null
  roleModelList: any[] | null
  status: string
  gmtCreated: number
  gmtModified: number
}

// 注册请求接口
export interface RegisterRequest {
  username: string
  password: string
}

// 登录请求接口
export interface LoginRequest {
  username: string // 支持用户名登录
  password: string
}

// 登录响应接口
export interface LoginResponse {
  token: string
  user: User
  expiresIn: number
}

// 服务端登录响应接口
export interface ServerLoginResponse {
  success: boolean
  code: number | null
  message: string | null
  result: string // JWT token
  serverTime: number
}

// 用户资料更新接口
export interface UpdateProfileRequest {
  username?: string
  email?: string
  avatar?: string
}

// 认证服务
export const authService = {
  // 用户注册
  register: async (data: RegisterRequest): Promise<User> => {
    try {
      const response = await HttpService.post<any>("/uc/register", data, { skipAuth: true })
      
      // 如果注册成功，返回用户信息
      if (response) {
        // 注册成功后，通常需要返回用户信息
        // 如果API没有直接返回用户信息，可以构造一个基本的用户对象
        return {
          id: 0, // 注册成功后通常需要重新登录获取完整用户信息
          account: data.username,
          nickname: null,
          gender: '',
          avatar: null,
          email: null,
          phoneNumber: null,
          phoneAreaCode: null,
          gmtBirthday: null,
          isBlocked: false,
          isLocked: false,
          realName: null,
          language: null,
          isUsernameFroze: false,
          isNeedDestroy: false,
          gmtDestroyTime: null,
          gmtDestroyRequest: null,
          isUnderDestroy: null,
          gmtDestroy: null,
          userMetaModel: null,
          departmentModelList: null,
          roleModelList: null,
          status: '',
          gmtCreated: Date.now(),
          gmtModified: Date.now()
        }
      }
      
      throw new Error('注册失败')
    } catch (error) {
      // 重新抛出错误，让上层处理
      throw error
    }
  },

  // 用户登录
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    try {
      const response = await HttpService.post<ServerLoginResponse>("/uc/login", data, { skipAuth: true })
      
      if (response.success && response.result) {
        // 从JWT token中解析用户信息
        const token = response.result
        const user = parseUserFromToken(token)
        const expiration = getTokenExpiration(token)
        
        return {
          token: token,
          user: user,
          expiresIn: expiration ? expiration - Math.floor(Date.now() / 1000) : 3600
        }
      } else {
        throw new Error(response.message || "登录失败")
      }
    } catch (error) {
      throw error
    }
  },

  // 获取用户资料
  getProfile: async (): Promise<User> => {
    const response = await HttpService.get("/uc/profile")
    return response.result
  },

  // 更新用户资料
  updateProfile: async (data: UpdateProfileRequest): Promise<User> => {
    return HttpService.put("/uc/profile", data)
  },

  // 修改密码
  changePassword: async (data: { oldPassword: string; newPassword: string }): Promise<ServerResponse<void>> => {
    return HttpService.post("/auth/change-password", data)
  },

  // 退出登录
  logout: async (): Promise<ServerResponse<void>> => {
    return HttpService.post("/auth/logout")
  },

  // 刷新token
  refreshToken: async (): Promise<ServerResponse<{ token: string; expiresIn: number }>> => {
    return HttpService.post("/auth/refresh")
  },
}

// 从JWT token中解析用户信息
function parseUserFromToken(token: string): User {
  const userInfo = extractUserFromToken(token)
  
  if (userInfo) {
    return {
      id: parseInt(userInfo.id) || 0,
      account: userInfo.username || '',
      nickname: null,
      gender: '',
      avatar: userInfo.avatar || null,
      email: userInfo.email || null,
      phoneNumber: null,
      phoneAreaCode: null,
      gmtBirthday: null,
      isBlocked: false,
      isLocked: false,
      realName: null,
      language: null,
      isUsernameFroze: false,
      isNeedDestroy: false,
      gmtDestroyTime: null,
      gmtDestroyRequest: null,
      isUnderDestroy: null,
      gmtDestroy: null,
      userMetaModel: null,
      departmentModelList: null,
      roleModelList: null,
      status: '',
      gmtCreated: Date.now(),
      gmtModified: Date.now()
    }
  }
  
  // 如果解析失败，返回默认用户信息
  return {
    id: 0,
    account: "用户",
    nickname: null,
    gender: '',
    avatar: null,
    email: null,
    phoneNumber: null,
    phoneAreaCode: null,
    gmtBirthday: null,
    isBlocked: false,
    isLocked: false,
    realName: null,
    language: null,
    isUsernameFroze: false,
    isNeedDestroy: false,
    gmtDestroyTime: null,
    gmtDestroyRequest: null,
    isUnderDestroy: null,
    gmtDestroy: null,
    userMetaModel: null,
    departmentModelList: null,
    roleModelList: null,
    status: '',
    gmtCreated: Date.now(),
    gmtModified: Date.now()
  }
}

// Token相关接口
export interface TokenBalance {
  balance: number
  currency: string
}

export interface TokenTransaction {
  id: string
  amount: number
  type: "purchase" | "consumption" | "refund"
  description: string
  createdAt: string
}

// Token服务
export const tokenService = {
  // 获取余额
  getBalance: async (): Promise<TokenBalance> => {
    return HttpService.get("/token/balance")
  },

  // 购买Token
  buyTokens: async (data: { amount: number; paymentMethod: string }): Promise<TokenTransaction> => {
    return HttpService.post("/token/buy", data)
  },

  // 获取交易历史
  getTransactions: async (params?: { page?: number; limit?: number }): Promise<TokenTransaction[]> => {
    return HttpService.get("/token/transactions", { params })
  },
}

// MCP相关接口
export interface MCP {
  id: string
  name: string
  description: string
  version: string
  author: User
  price: number
  downloads: number
  rating: number
  status: "draft" | "submitted" | "published" | "rejected"
  createdAt: number
  updatedAt: number
}

export interface MCPVersion {
  id: string
  version: string
  changelog: string
  createdAt: number
}

// MCP服务
export const mcpService = {
  // 获取用户MCP列表
  getUserMCPs: async (): Promise<MCP[]> => {
    return HttpService.get("/mcp/user")
  },

  // 获取MCP详情
  getMCP: async (id: string): Promise<MCP> => {
    return HttpService.get(`/mcp/${id}`)
  },

  // 上传MCP文件
  uploadMCP: async (file: File): Promise<MCP> => {
    return HttpService.upload("/mcp/upload", file)
  },

  // 测试MCP
  testMCP: async (id: string): Promise<{ success: boolean; message: string }> => {
    return HttpService.get(`/mcp/test/${id}`)
  },

  // 获取MCP版本列表
  getMCPVersions: async (id: string): Promise<MCPVersion[]> => {
    return HttpService.get(`/mcp/${id}/versions`)
  },

  // 创建新版本
  createMCPVersion: async (id: string, data: { version: string; changelog: string }): Promise<MCPVersion> => {
    return HttpService.post(`/mcp/${id}/version`, data)
  },

  // 提交审核
  submitMCP: async (id: string): Promise<void> => {
    return HttpService.post(`/mcp/${id}/submit`)
  },

  // 发布MCP
  publishMCP: async (id: string, data: { tokens: number }): Promise<void> => {
    return HttpService.post(`/mcp/${id}/publish`, data)
  },

  // 获取SDK文档
  getSDKDocs: async (): Promise<{ content: string; version: string }> => {
    return HttpService.get("/mcp/sdk-docs")
  },

  // 搜索MCP
  searchMCPs: async (params: { 
    query?: string; 
    category?: string; 
    price?: "free" | "paid"; 
    sort?: "newest" | "popular" | "rating";
    page?: number;
    limit?: number;
  }): Promise<{ items: MCP[]; total: number; page: number; limit: number }> => {
    return HttpService.get("/mcp/search", { params })
  },

  // 购买MCP
  purchaseMCP: async (id: string): Promise<{ transactionId: string }> => {
    return HttpService.post(`/mcp/${id}/purchase`)
  },
}

// 市场相关接口
export interface MarketplaceStats {
  totalMCPs: number
  totalUsers: number
  totalDownloads: number
  featuredMCPs: MCP[]
}

// 市场服务
export const marketplaceService = {
  // 获取市场统计
  getStats: async (): Promise<MarketplaceStats> => {
    return HttpService.get("/marketplace/stats")
  },

  // 获取热门MCP
  getPopularMCPs: async (limit: number = 10): Promise<MCP[]> => {
    return HttpService.get("/marketplace/popular", { params: { limit } })
  },

  // 获取最新MCP
  getLatestMCPs: async (limit: number = 10): Promise<MCP[]> => {
    return HttpService.get("/marketplace/latest", { params: { limit } })
  },

  // 获取推荐MCP
  getRecommendedMCPs: async (limit: number = 10): Promise<MCP[]> => {
    return HttpService.get("/marketplace/recommended", { params: { limit } })
  },
}

// Agent相关接口
export interface AgentModel {
  id: string
  name: string
  description?: string
  provider?: string
  version?: string
  capabilities?: string[]
  maxTokens?: number
  isActive?: boolean
  createdAt?: number
  updatedAt?: number
}

// Agent实体接口
export interface Agent {
  id: number | string
  name: string
  description: string
  status: 'active' | 'inactive' | 'draft' | 'error' | 'NORMAL'
  agentType?: 'conversation' | 'file_conversion' | 'image_generation' | 'code_assistant' | null
  agentStatus: 'QUEUED' | 'BUILDING' | 'RUNNING' | 'NORMAL' | 'ERROR' | 'DRAFT' | 'INACTIVE' | 'ACTIVE' | 'FAILED' | 'READY'
  enabled?: boolean
  sessionId?: string
  prompt?: string | null
  userId?: number
  gmtCreated?: number
  gmtModified?: number
  usageCount?: number
  lastUsed?: string
  model?: string
  tags?: string[]
  config?: {
    modelId?: string
    prompt?: string
    parameters?: Record<string, any>
  }
}

// 创建Agent请求接口
export interface CreateAgentRequest {
  agentName: string
  description: string
  agentType: 'conversation' | 'file_conversion' | 'image_generation' | 'code_assistant'
  modelId?: string
  prompt?: string
  tags?: string[]
  config?: Record<string, any>
  sessionId: string // 新增
}

// 更新Agent请求接口
export interface UpdateAgentRequest {
  name?: string
  description?: string
  status?: 'active' | 'inactive' | 'draft' | 'error'
  agentType?: 'conversation' | 'file_conversion' | 'image_generation' | 'code_assistant'
  modelId?: string
  prompt?: string
  tags?: string[]
  config?: Record<string, any>
}

// Agent会话相关接口
export interface AgentSession {
  sessionId: string
}

export interface AgentMessage {
  id: string
  sessionId: string
  type: 'user' | 'ai'
  content: string
  timestamp: string
  metadata?: {
    modelId?: string
    tokens?: number
    processingTime?: number
  }
}

export interface AgentSessionHistory {
  messages: Array<{
    role: 'user' | 'assistant'
    content: string | null
    gmtCreated: string
    messageId: string
  }>
  agentId?: string | null
}

// Agent服务
export const agentService = {
  // 获取Agent列表
  getAgentList: async (params?: { 
    page?: number; 
    limit?: number; 
    status?: string;
    type?: string;
  }): Promise<{ items: Agent[]; total: number; page: number; limit: number }> => {
    const response = await HttpService.get("/agent/list", { params })
    console.log('getAgentList 原始响应:', response)
    
    // HttpService已经提取了data字段，所以response直接是data的内容
    // 根据新的API格式，数据在 response.result 中
    if (response?.result) {
      const result = response.result
      console.log('找到新的API格式，result:', result)
      console.log('content:', result.content)
      const processed = {
        items: result.content || [],
        total: result.total || 0,
        page: result.page || 0,
        limit: result.pageSize || 10
      }
      console.log('处理后的数据:', processed)
      return processed
    }
    
    // 兼容旧格式
    console.log('使用旧格式处理')
    return response.result || response
  },

  // 获取单个Agent详情
  getAgent: async (id: string): Promise<Agent> => {
    const response = await HttpService.get(`/agent/item?id=${id}`)
    console.log('getAgent 原始响应:', response)
    
    // 根据新的API格式，数据在 response.result.result 中
    if (response?.result?.result) {
      return response.result.result
    }
    
    // 兼容旧格式
    return response.result || response
  },

  // 获取Agent状态
  getAgentStatus: async (id: string): Promise<any> => {
    const response = await HttpService.get(`/agent/item/status?id=${id}`)
    return response.result;
  },

  // 创建Agent
  createAgent: async (data: CreateAgentRequest): Promise<Agent> => {
    const response = await HttpService.post("/agent/create", data)
    console.log('createAgent 原始响应:', response)
    
    // 根据新的API格式，数据可能在 response.result.result 中
    if (response?.result?.result) {
      return response.result.result
    }
    
    // 兼容旧格式
    return response.result || response
  },

  // 更新Agent
  updateAgent: async (id: string, data: UpdateAgentRequest): Promise<Agent> => {
    const response = await HttpService.put(`/agent/${id}`, data)
    console.log('updateAgent 原始响应:', response)
    
    // 根据新的API格式，数据可能在 response.result.result 中
    if (response?.result?.result) {
      return response.result.result
    }
    
    // 兼容旧格式
    return response.result || response
  },

  // 更新Agent项目（使用POST /agent/item/update）
  updateAgentItem: async (id: string, data: UpdateAgentRequest, rebuild: boolean = false): Promise<Agent> => {
    const requestData = { ...data, id, rebuild }
    const response = await HttpService.post(`/agent/item/update?id=${id}`, requestData)
    console.log('updateAgentItem 原始响应:', response)
    
    // 根据新的API格式，数据在 response.data.result 中
    if (response?.data?.result) {
      return response.data.result
    }
    
    // 兼容旧格式
    if (response?.result?.result) {
      return response.result.result
    }
    
    // 如果直接返回结果
    if (response?.result) {
      return response.result
    }
    
    // 默认返回
    return response
  },

  // 删除Agent
  deleteAgent: async (id: string | number): Promise<void> => {
    const response = await HttpService.delete(`/agent/${id}`)
    console.log('deleteAgent 原始响应:', response)
    
    // 根据新的API格式，数据可能在 response.result.result 中
    if (response?.result?.result !== undefined) {
      return response.result.result
    }
    
    // 兼容旧格式
    return response.result || response
  },

  // 切换Agent状态
  toggleAgentStatus: async (id: string | number, status: 'active' | 'inactive'): Promise<Agent> => {
    const response = await HttpService.patch(`/agent/${id}/status`, { status })
    console.log('toggleAgentStatus 原始响应:', response)
    
    // 根据新的API格式，数据可能在 response.result.result 中
    if (response?.result?.result) {
      return response.result.result
    }
    
    // 兼容旧格式
    return response.result || response
  },

  // 获取模型列表（带缓存）
  getModels: async (): Promise<AgentModel[]> => {
    // 检查缓存
    const cached = localStorage.getItem('agent_models_cache')
    const cacheTime = localStorage.getItem('agent_models_cache_time')
    
    // 缓存有效期为5分钟
    const CACHE_DURATION = 5 * 60 * 1000
    const now = Date.now()
    
    if (cached && cacheTime && (now - parseInt(cacheTime)) < CACHE_DURATION) {
      try {
        return JSON.parse(cached)
      } catch (error) {
        console.warn('Failed to parse cached models, fetching fresh data')
      }
    }
    
    try {
      const response = await HttpService.get("/agent/models")
      
      // 根据实际返回的数据结构处理
      // 数据可能在response.result中，也可能在response中
      const modelNames = response?.result || response || []
      
      // 确保 modelNames 是数组
      if (!Array.isArray(modelNames)) {
        console.error('Invalid model names format:', modelNames)
        return []
      }
      
      const models: AgentModel[] = modelNames.map((name: string, index: number) => ({
        id: name.toLowerCase().replace(/\s+/g, '-'),
        name: name,
        description: `AI model: ${name}`,
        provider: 'Unknown',
        version: '1.0',
        capabilities: ['chat'],
        maxTokens: 4096,
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }))
      
      // 更新缓存
      localStorage.setItem('agent_models_cache', JSON.stringify(models))
      localStorage.setItem('agent_models_cache_time', now.toString())
      
      return models
    } catch (error) {
      console.error('Failed to fetch models:', error)
      // 如果API失败但有缓存，返回缓存数据
      if (cached) {
        try {
          return JSON.parse(cached)
        } catch (parseError) {
          console.error('Failed to parse cached models:', parseError)
        }
      }
      // 如果API失败且没有缓存，抛出错误
      throw error
    }
  },

  // 清除模型缓存
  clearModelCache: () => {
    localStorage.removeItem('agent_models_cache')
    localStorage.removeItem('agent_models_cache_time')
  },

  // 刷新模型列表
  refreshModels: async (): Promise<AgentModel[]> => {
    agentService.clearModelCache()
    return agentService.getModels()
  },

  // 获取或创建当前会话
  getCurrentSession: async (): Promise<AgentSession> => {
    // 优先从 localStorage 读取
    const localSessionId = localStorage.getItem('agent_session_id')
    let payload: any = {}
    if (localSessionId) {
      payload.sessionId = localSessionId
    }
    const response = await HttpService.post("/agent/chat/session", payload)
    // 根据接口返回结构，response.result 才是 session 对象
    const session = response.result
    // 保存最新 sessionId
    if (session && session.sessionId) {
      localStorage.setItem('agent_session_id', session.sessionId)
    }
    return session
  },

  // 获取会话历史
  getSessionHistory: async (sessionId: string): Promise<AgentSessionHistory> => {
    const response = await HttpService.get(`/agent/user/session/his?sessionId=${sessionId}`)
    console.log('getSessionHistory 原始响应:', response)
    
    // 根据服务端返回结构，数据在response.result中
    const result = response?.result || response
    
    // 如果result有history字段，返回history
    if (result && result.history) {
      return { 
        messages: result.history,
        agentId: result.agentId || null
      }
    }
    
    // 如果response本身就是result数组，直接返回
    if (Array.isArray(response)) {
      return { 
        messages: response,
        agentId: null
      }
    }
    
    // 如果response有result字段，返回result
    if (response && response.result) {
      return { 
        messages: response.result,
        agentId: null
      }
    }
    
    // 如果response有data.result字段，返回data.result
    if (response && response.data && response.data.result) {
      return { 
        messages: response.data.result,
        agentId: null
      }
    }
    
    // 默认返回空数组
    return { 
      messages: [],
      agentId: null
    }
  },

  // 发送消息到会话（流式API）
  sendMessage: async (
    sessionId: string, 
    message: { content: string; modelId?: string },
    onChunk?: (chunk: { type: string; content: string; model?: any }) => void
  ): Promise<AgentMessage> => {
    const token = localStorage.getItem("token")
    
    // 构建请求体，将 modelId 转换为 model，content 转换为 message
    const requestBody: any = {
      sessionId,
      message: message.content
    }
    
    // 如果有模型ID，转换为 model 字段
    if (message.modelId) {
      requestBody.model = message.modelId
    }
    
    const response = await fetch(`${API_BASE_URL}/agent/chat/send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Biz-Id': 'agentcreator'
      },
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('No response body')
    }

    const decoder = new TextDecoder()
    let fullContent = ''
    let messageId = `msg-${Date.now()}`
    let buffer = ''

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) {
          // Process any remaining data in the buffer when the stream is closed.
          if (buffer.startsWith('data:')) {
            const jsonData = buffer.slice(5).trim()
            if (jsonData) {
              try {
                const data = JSON.parse(jsonData)
                if (onChunk) onChunk(data)
                if (data.content) fullContent += data.content
              } catch (e) {
                console.warn('Failed to parse final SSE data:', buffer, e)
              }
            }
          }
          break
        }

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || '' // Keep the last (potentially incomplete) line in the buffer

        for (const line of lines) {
          if (line.startsWith('data:')) {
            const jsonData = line.slice(5).trim()
            if (jsonData) {
              try {
                const data = JSON.parse(jsonData)
                if (data.type === 'start') {
                  if (onChunk) onChunk(data)
                } else if (data.type === 'chunk') {
                  fullContent += data.content
                  if (onChunk) onChunk(data)
                } else if (data.type === 'end') {
                  if (onChunk) onChunk(data)
                }
              } catch (e) {
                console.warn('Failed to parse SSE data:', line, e)
              }
            }
          }
        }
      }
    } finally {
      const finalChunk = decoder.decode()
      if (finalChunk) {
        buffer += finalChunk
        if (buffer.startsWith('data:')) {
          const jsonData = buffer.slice(5).trim()
          if (jsonData) {
            try {
              const data = JSON.parse(jsonData)
              if (onChunk) onChunk(data)
              if (data.content) fullContent += data.content
            } catch (e) {
              console.warn('Failed to parse final SSE data:', buffer, e)
            }
          }
        }
      }
      reader.releaseLock()
    }

    // 返回完整的消息对象
    return {
      id: messageId,
      sessionId,
      type: 'ai',
      content: fullContent,
      timestamp: new Date().toISOString(),
      metadata: {
        modelId: message.modelId
      }
    }
  },

  // 创建新会话
  createSession: async (data: { title: string; modelId: string }): Promise<AgentSession> => {
    return HttpService.post("/agent/chat/session", data)
  },

  // 创建新用户会话
  createNewSession: async (data: { prompt: string }): Promise<AgentSession> => {
    const response = await HttpService.post("/agent/user/session/new", data)
    // 根据实际返回结构，sessionId在 data.result.sessionId 中
    // HttpService.post 返回的是 response.data.data，所以这里需要访问 result.sessionId
    return {
      sessionId: response.result?.sessionId
    }
  },

  // 更新会话标题
  updateSessionTitle: async (sessionId: string, title: string): Promise<AgentSession> => {
    return HttpService.put(`/agent/chat/session/${sessionId}`, { title })
  },

  // 归档会话
  archiveSession: async (sessionId: string): Promise<void> => {
    return HttpService.post(`/agent/chat/session/${sessionId}/archive`)
  },

  // 删除会话
  deleteSession: async (sessionId: string): Promise<void> => {
    return HttpService.delete(`/agent/chat/session/${sessionId}`)
  },

  // 获取最近会话列表
  getSessionList: async (params?: { page?: number; limit?: number }): Promise<any[]> => {
    const response = await HttpService.get("/agent/user/session/list", { params })
    if (Array.isArray(response?.result)) {
      return response.result
    }
    return []
  },
}

// 导出默认API实例（保持向后兼容）
export default {
  auth: authService,
  token: tokenService,
  mcp: mcpService,
  marketplace: marketplaceService,
  agent: agentService,
}
